import { useState } from "react";
import { toast } from "sonner";
import { Trash2, RotateCcw, Download } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { cedi, fmtDate, showPin, api, errMsg, overpaymentOf } from "@/lib/api";

export const DeleteRegButton = ({ r, onChanged }) => {
  const [open, setOpen] = useState(false);
  const paid = (r.paid || 0) > 0;
  const go = async () => {
    try {
      await api.delete(`/transactions/${r.id}`);
      toast.success(`Registration for ${r.delegate_name} moved to Deleted`);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="text-xs font-semibold text-gray-500 hover:text-[#E60000] inline-flex items-center gap-1" data-testid={`evreg-delete-${r.ticket_no}`}><Trash2 className="w-3.5 h-3.5" />Delete</button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="evreg-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{paid ? "Refund needed first" : "Delete this registration?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {paid ? `${cedi(r.paid)} has been paid by ${r.delegate_name}. An Accounts Officer must refund it in Payments & Receipts before this registration can be deleted.`
                : `${r.delegate_name}'s registration (${r.ticket_no}) will move to the Deleted tab. You can undelete it there.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="evreg-delete-cancel">{paid ? "OK" : "No"}</AlertDialogCancel>
            {!paid && <AlertDialogAction onClick={go} className="bg-[#E60000] hover:bg-[#CC0000]" data-testid="evreg-delete-confirm">Yes, delete</AlertDialogAction>}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export const PermanentlyDeleteRegButton = ({ r, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.delete(`/transactions/${r.id}/permanent`, { data: { confirmed: true } });
      toast.success("Registration and linked payment history permanently deleted");
      setOpen(false); onChanged();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return <>
    <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1 rounded px-2 py-1.5 text-xs font-semibold bg-[#E60000] text-white hover:bg-[#CC0000]" data-testid={`evreg-permanent-${r.ticket_no}`}><Trash2 className="w-3.5 h-3.5" />Permanently delete</button>
    <AlertDialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
      <AlertDialogContent className="w-[calc(100%-2rem)] rounded-lg max-h-[90vh] overflow-y-auto" data-testid="evreg-permanent-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="evreg-permanent-title">Permanently delete this registration?</AlertDialogTitle>
          <AlertDialogDescription className="text-gray-700" data-testid="evreg-permanent-description">
            {r.delegate_name} · {r.ticket_no}. This will erase the registration and all linked payment, refund and receipt history, plus its matching draft and booking. Cash report history will change. This cannot be undone and does not issue a refund. The member account and other registrations are kept.
          </AlertDialogDescription>
          <p className="text-sm font-semibold text-red-800 bg-red-50 border border-red-200 rounded p-3" data-testid="evreg-permanent-paid">Amount still paid: {cedi(r.paid || 0)}</p>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2 sm:space-x-0">
          <AlertDialogCancel className="mt-0" disabled={busy} data-testid="evreg-permanent-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={busy} onClick={go} className="bg-[#E60000] hover:bg-[#CC0000]" data-testid="evreg-permanent-confirm">{busy ? "Deleting…" : "Permanently delete"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>;
};


const Restore = ({ r, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/transactions/${r.id}/restore`);
      toast.success(`Registration for ${r.delegate_name} restored`);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return <button onClick={go} disabled={busy} className="text-xs font-semibold text-emerald-700 hover:underline inline-flex items-center gap-1 disabled:opacity-50" data-testid={`evreg-undelete-${r.ticket_no}`}><RotateCcw className="w-3.5 h-3.5" />Undelete</button>;
};

export const SelectBox = ({ checked, onChange, testId, label }) => (
  <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} aria-label={label} data-testid={testId} className="border-gray-400 data-[state=checked]:bg-[#E60000] data-[state=checked]:border-[#E60000]" />
);

const ConfirmBulk = ({ open, setOpen, count, onYes }) => (
  <AlertDialog open={open} onOpenChange={setOpen}>
    <AlertDialogContent data-testid="evreg-bulk-delete-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>Delete {count} registration{count === 1 ? "" : "s"}?</AlertDialogTitle>
        <AlertDialogDescription>They will move to the Deleted tab. You can undelete them there.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="evreg-bulk-delete-cancel">No</AlertDialogCancel>
        <AlertDialogAction onClick={onYes} className="bg-[#E60000] hover:bg-[#CC0000]" data-testid="evreg-bulk-delete-confirm">Yes, delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const BulkBar = ({ ids, deleted, canDelete, onExport, onClear, onChanged, evId }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async (path, msg) => {
    setBusy(true);
    try {
      const r = await api.post(path, { ids });
      if (r.data.failed?.length) toast.error(`${r.data.failed.length} could not be undeleted: ${r.data.failed[0]}`);
      toast.success(msg(r.data));
      onClear();
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md bg-[#111] text-white px-4 py-2.5 text-sm" data-testid={`evreg-bulk-bar-${evId}`}>
      <span className="font-semibold" data-testid={`evreg-bulk-count-${evId}`}>{ids.length} selected</span>
      {!deleted && onExport && <button onClick={onExport} className="inline-flex items-center gap-1.5 font-semibold hover:text-[#FF4D4D]" data-testid={`evreg-bulk-export-${evId}`}><Download className="w-4 h-4" />Export selected</button>}
      {canDelete && !deleted && <button onClick={() => setOpen(true)} disabled={busy} className="inline-flex items-center gap-1.5 font-semibold hover:text-[#FF4D4D] disabled:opacity-50" data-testid={`evreg-bulk-delete-${evId}`}><Trash2 className="w-4 h-4" />Delete selected</button>}
      {canDelete && deleted && <button onClick={() => run("/transactions/bulk-restore", (d) => `${d.restored} registration(s) restored`)} disabled={busy} className="inline-flex items-center gap-1.5 font-semibold hover:text-emerald-300 disabled:opacity-50" data-testid={`evreg-bulk-undelete-${evId}`}><RotateCcw className="w-4 h-4" />Undelete selected</button>}
      <button onClick={onClear} className="ml-auto text-white/70 hover:text-white text-xs font-semibold" data-testid={`evreg-bulk-clear-${evId}`}>Clear</button>
      <ConfirmBulk open={open} setOpen={setOpen} count={ids.length} onYes={() => run("/transactions/bulk-delete", (d) => `${d.deleted} registration(s) moved to Deleted`)} />
    </div>
  );
};

export const DeletedTable = ({ rows, canDelete, canPermanent, onChanged, sel }) => (
  <table className="w-full text-sm">
    <thead className="bg-[#F8F9FA] text-[11px] uppercase tracking-wider text-gray-500">
      <tr><th className="px-3 py-2.5 w-8"><SelectBox checked={sel.all(rows)} onChange={(v) => sel.setAll(rows, v)} label="Select all" testId="evreg-deleted-select-all" /></th>{["Ticket No", "Participant", "Category", "Amount", "Status", "Deleted", ""].map((h) => <th key={h} className="text-left font-bold px-3 py-2.5 whitespace-nowrap">{h}</th>)}</tr>
    </thead>
    <tbody className="divide-y">
      {rows.map((r) => (
        <tr key={r.id} className="text-gray-600" data-testid={`evreg-deleted-row-${r.ticket_no}`}>
          <td className="px-3 py-2.5"><SelectBox checked={sel.has(r.id)} onChange={(v) => sel.set(r.id, v)} label={`Select ${r.delegate_name}`} testId={`evreg-deleted-select-${r.ticket_no}`} /></td>
          <td className="px-3 py-2.5 font-mono text-xs whitespace-nowrap">{r.ticket_no}</td>
          <td className="px-3 py-2.5 whitespace-nowrap"><div className="font-medium text-[#111]">{r.delegate_name}</div><div className="font-mono text-[11px]">{showPin(r.pin)}</div></td>
          <td className="px-3 py-2.5 whitespace-nowrap">{r.category_name}</td>
          <td className="px-3 py-2.5 font-mono text-xs whitespace-nowrap">{cedi(r.amount)}{overpaymentOf(r) > 0 && <div className="text-amber-800" data-testid={`evreg-deleted-overpayment-${r.ticket_no}`}>Overpayment {cedi(overpaymentOf(r))}</div>}</td>
          <td className="px-3 py-2.5 text-xs">{r.status}</td>
          <td className="px-3 py-2.5 text-xs whitespace-nowrap">{fmtDate(r.deleted_at)}{r.deleted_by && <div className="text-gray-400">by {r.deleted_by}</div>}</td>
          <td className="px-3 py-2.5 text-right whitespace-nowrap space-x-3">{canDelete && <Restore r={r} onChanged={onChanged} />}{canPermanent && <PermanentlyDeleteRegButton r={r} onChanged={onChanged} />}</td>
        </tr>
      ))}
    </tbody>
  </table>
);
